import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Wand2, Trophy, Save, BarChart3 } from "lucide-react";
import api, { API } from "@/lib/api";

function VariantRow({ v, onSaved }) {
  const [m, setM] = useState(v.metrics);
  const save = async () => {
    try { await api.put(`/admin/ad-variants/${v.id}/metrics`, m); toast.success("Metrics saved"); onSaved(); }
    catch { toast.error("Failed"); }
  };
  const del = async () => { await api.delete(`/admin/ad-variants/${v.id}`); onSaved(); };
  const field = (k, label) => (
    <label className="text-xs text-ink/60 flex flex-col">
      {label}
      <input type="number" min="0" step={k === "spend" ? "0.01" : "1"} className="brut-input !py-1 !w-24 text-sm"
        value={m[k]} onChange={(e) => setM({ ...m, [k]: k === "spend" ? parseFloat(e.target.value || "0") : parseInt(e.target.value || "0") })} />
    </label>
  );
  return (
    <div className={`brut border-ink p-4 shadow-brut ${v.is_winner ? "bg-green-100" : "bg-white"}`} data-testid={`variant-${v.id.slice(0, 8)}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="font-display font-bold flex items-center gap-2">
            {v.is_winner && <span className="tag-pill bg-sun !py-0.5"><Trophy className="w-3.5 h-3.5" /> Winner</span>}
            {v.headline}
          </div>
          <p className="text-sm text-ink/70 mt-1">{v.body}</p>
        </div>
        {v.image_file_id && <img src={`${API}/files/${v.image_file_id}`} alt="" className="w-20 h-20 object-cover brut border-ink" />}
        <button onClick={del} className="text-tangerine"><Trash2 className="w-4 h-4" /></button>
      </div>
      <div className="flex flex-wrap items-end gap-3 mt-3 pt-3 border-t-2 border-dashed border-ink/20">
        {field("impressions", "Impressions")}
        {field("clicks", "Clicks")}
        {field("conversions", "Conversions")}
        {field("spend", "Spend ($)")}
        <button onClick={save} className="brut-btn !py-1.5 !px-3 bg-green-300 text-xs" data-testid={`save-metrics-${v.id.slice(0, 8)}`}><Save className="w-3.5 h-3.5" /> Save</button>
      </div>
      <div className="flex gap-4 mt-3 text-sm font-bold">
        <span className="tag-pill bg-sky/40">CTR {v.ctr}%</span>
        <span className="tag-pill bg-grape/30">CVR {v.cvr}%</span>
        <span className="tag-pill bg-tangerine/30">CPA ${v.cpa}</span>
      </div>
    </div>
  );
}

function CampaignCard({ c, onChange }) {
  const [product, setProduct] = useState("");
  const [busy, setBusy] = useState(false);
  const [newV, setNewV] = useState({ headline: "", body: "" });

  const genVariants = async () => {
    if (!product.trim()) return toast.error("What product are we advertising?");
    setBusy(true);
    try {
      const r = await api.post("/admin/ai/ad-copy", { product, count: 3 });
      for (const v of r.data.variants) {
        await api.post("/admin/ad-variants", { campaign_id: c.id, headline: v.headline, body: v.body });
      }
      toast.success(`Added ${r.data.variants.length} AI variants`); setProduct(""); onChange();
    } catch { toast.error("Generation failed"); } finally { setBusy(false); }
  };
  const addManual = async () => {
    if (!newV.headline.trim()) return toast.error("Headline required");
    await api.post("/admin/ad-variants", { campaign_id: c.id, ...newV });
    setNewV({ headline: "", body: "" }); onChange();
  };
  const delCampaign = async () => { if (window.confirm("Delete campaign?")) { await api.delete(`/admin/ad-campaigns/${c.id}`); onChange(); } };

  return (
    <div className="brut-card p-5" data-testid={`campaign-${c.id.slice(0, 8)}`}>
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <span className="grid place-items-center w-10 h-10 rounded-xl bg-tangerine border-2 border-ink"><BarChart3 className="w-5 h-5 text-white" /></span>
          <div>
            <div className="font-display font-bold text-lg">{c.name}</div>
            <div className="text-sm text-ink/60 capitalize">{c.objective} · {c.variants.length} variants</div>
          </div>
        </div>
        <button onClick={delCampaign} className="text-tangerine"><Trash2 className="w-4 h-4" /></button>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <input className="brut-input !py-2 flex-1 min-w-[180px] text-sm" placeholder="Product to advertise (for AI copy)…"
          value={product} onChange={(e) => setProduct(e.target.value)} data-testid={`ad-product-${c.id.slice(0, 8)}`} />
        <button onClick={genVariants} disabled={busy} className="brut-btn !py-2 !px-4 bg-bubble text-white text-sm" data-testid={`gen-adcopy-${c.id.slice(0, 8)}`}>
          <Wand2 className="w-4 h-4" /> {busy ? "Writing…" : "AI variants"}
        </button>
      </div>

      <div className="space-y-3">
        {c.variants.map((v) => <VariantRow key={v.id} v={v} onSaved={onChange} />)}
      </div>

      <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t-2 border-dashed border-ink/20">
        <input className="brut-input !py-2 text-sm w-40" placeholder="Headline" value={newV.headline} onChange={(e) => setNewV({ ...newV, headline: e.target.value })} />
        <input className="brut-input !py-2 flex-1 min-w-[160px] text-sm" placeholder="Body" value={newV.body} onChange={(e) => setNewV({ ...newV, body: e.target.value })} />
        <button onClick={addManual} className="brut-btn !py-2 !px-4 bg-sky text-sm"><Plus className="w-4 h-4" /> Add</button>
      </div>
    </div>
  );
}

export default function AdsTab() {
  const [campaigns, setCampaigns] = useState([]);
  const [name, setName] = useState("");
  const load = () => api.get("/admin/ad-campaigns").then((r) => setCampaigns(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) return toast.error("Name your campaign");
    await api.post("/admin/ad-campaigns", { name, objective: "conversions" });
    setName(""); load();
  };

  return (
    <div data-testid="admin-ads">
      <h1 className="font-display font-bold text-3xl mb-1">Ad Optimizer</h1>
      <p className="text-ink/60 mb-6">Generate ad variants with AI, log their performance, and let the tracker crown a winner (needs 100+ impressions).</p>

      <div className="brut-card p-4 mb-6 flex flex-wrap gap-2">
        <input className="brut-input !py-2 flex-1 min-w-[200px] text-sm" placeholder="New campaign name (e.g. 'Holiday 2025')"
          value={name} onChange={(e) => setName(e.target.value)} data-testid="campaign-name" />
        <button onClick={create} className="brut-btn !py-2 !px-4 bg-bubble text-white text-sm" data-testid="create-campaign"><Plus className="w-4 h-4" /> Create campaign</button>
      </div>

      <div className="space-y-5">
        {campaigns.length === 0 && <div className="brut-card p-6 text-ink/60">No campaigns yet. Create one to start A/B testing ad copy.</div>}
        {campaigns.map((c) => <CampaignCard key={c.id} c={c} onChange={load} />)}
      </div>
    </div>
  );
}
